import axios, { AxiosResponse } from 'axios';
import * as api from './middleware/api.middleware';
import { staticConfig } from '../config/static';
import {
    IArtifact,
    ICreateArtifactRequest,
    ICreateArtifactRequestFile,
    IFileContent,
} from '../interfaces/artifact.interfaces';
import { IResponseCollection } from '../interfaces/general.interfaces';

import { getMiddlewareOptionsWithContext, getEncondingFormat } from './middleware/helpers';
import { ArtifactType, ErrorContext, SupportedMimeType } from '../enums';

const serviceUrl = '/artifactservice/v2';

export function getArtifactsByMessageId(
    mailboxId: string,
    messageId: string,
    language: string,
    expandWithBlob = false,
    context?: ErrorContext
): Promise<IResponseCollection<IArtifact>> {
    let url = serviceUrl + `/artifacts?messageId=${messageId}&mailboxId=${mailboxId}&languageId=${language}`;
    if (expandWithBlob) {
        url = url + '&$expands=blob';
    }

    return api.get<IResponseCollection<IArtifact>>(url, null, getMiddlewareOptionsWithContext(context));
}

export const getBlob = (
    mailboxId: string,
    documentId: string,
    artifactId: string,
    fileId: string,
    mimeType: string
): Promise<Blob> => {
    const url =
        staticConfig.apiBaseUrl +
        serviceUrl +
        `/mailboxes/${mailboxId}/messages/${documentId}/artifacts/${artifactId}/files/${fileId}/content?mimeType=${mimeType}`;

    return new Promise<Blob>((resolve, reject) => {
        fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': mimeType },
            credentials: 'include',
        })
            .then((response) => response.blob())
            .then((blob) => {
                resolve(blob);
            })
            .catch((err) => {
                reject(err);
            });
    });
};

export function uploadBlob(
    formData: FormData,
    mailboxId: string,
    documentId: string,
    artifactId: string,
    fileId: string,
    version: number
): Promise<IFileContent> {
    const url =
        serviceUrl +
        `/mailboxes/${mailboxId}/messages/${documentId}/artifacts/${artifactId}/files/${fileId}/content?version=${version}`;
    return api.putFormData<IFileContent>(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } }, null);
}

export function createDocument(
    mailboxId: string,
    documentId: string,
    languageId: string,
    createArtifactRequest: ICreateArtifactRequest
): Promise<IArtifact> {
    const url = serviceUrl + `/mailboxes/${mailboxId}/messages/${documentId}/artifacts?languageId=${languageId}`;
    return api.post<IArtifact>(url, createArtifactRequest);
}

export function deleteArtifact(
    mailboxId: string,
    documentId: string,
    artifactId: string,
    version: number
): Promise<unknown> {
    const url =
        serviceUrl + `/mailboxes/${mailboxId}/messages/${documentId}/artifacts/${artifactId}?version=${version}`;
    return api.del(url);
}

export function downloadBlob(
    mailboxId: string,
    documentId: string,
    artifactId: string,
    fileId: string,
    mimeType: string,
    fileName: string
): void {
    getBlob(mailboxId, documentId, artifactId, fileId, mimeType).then((blob) => {
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(a.href);
        a.remove();
    });
}

export function uploadFileFromBody(mailboxId: string, artifact: IArtifact, bodyText: string): Promise<IFileContent> {
    const formData = new FormData();
    const bodyFile = new File([bodyText], artifact.files[0].filename, { type: SupportedMimeType.HTML });
    formData.append('file', bodyFile, bodyFile.name);
    return uploadFile(mailboxId, artifact, formData);
}

export function uploadFileFromFile(mailboxId: string, artifact: IArtifact, file: File): Promise<IFileContent> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return uploadFile(mailboxId, artifact, formData);
}

export function uploadFile(mailboxId: string, artifact: IArtifact, formData: FormData): Promise<IFileContent> {
    return uploadBlob(
        formData,
        mailboxId,
        artifact.messageId,
        artifact.id,
        artifact.files[0].id,
        artifact.files[0].version
    );
}

export const uploadFiles = (
    mailboxId: string,
    artifact: IArtifact,
    file: File,
    onUploadProgress?: (progressEvent: ProgressEvent) => void,
    signal?: AbortSignal
): Promise<AxiosResponse<IFileContent>> => {
    const { version, id } = artifact.files[0];

    const url = `${staticConfig.apiBaseUrl + serviceUrl}/mailboxes/${mailboxId}/messages/${
        artifact.messageId
    }/artifacts/${artifact.id}/files/${id}/content?version=${version}`;

    const formData = new FormData();
    formData.append('file', file, file.name);

    return axios.put(url, formData, {
        signal,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
        withCredentials: true,
    });
};

export function createArtifact(mailboxId: string, messageId: string, file: File, language: string): Promise<IArtifact> {
    const createArtifactRequestFile: ICreateArtifactRequestFile = {
        id: null,
        encodingFormat: getEncondingFormat(file),
        filename: file.name,
        language: language,
    };
    const createArtifactRequest: ICreateArtifactRequest = {
        actions: [],
        documentType: ArtifactType.MESSAGE_DOCUMENT,
        label: file.name,
        files: [createArtifactRequestFile],
    };
    return createDocument(mailboxId, messageId, language, createArtifactRequest);
}

/**
 * Hide "download" and "information" on documents
 * https://jira.e-boks.dk/browse/FREM-1603
 * Re-enable as part of https://jira.e-boks.dk/browse/FREM-693
 */

//  import {
//     IArtifact,
//     IArtifactTokenResponse,
//     ICreateArtifactRequest,
//     ICreateArtifactRequestFile,
//     IFileContent,
// } from '../interfaces/artifact.interfaces';

// import { buildIdsQueryParamString } from '../helpers/general';

// export function getDownloadDocumentsArchiveUrl(ids: Array<string>, token: string): string {
//     const idQueryString = 'id=' + buildIdsQueryParamString(ids);
//     const tokenQueryString = '&token=' + encodeURIComponent(token);
//     return staticConfig.apiBaseUrl + serviceUrl + '/artifacts/download-with-token?' + idQueryString + tokenQueryString;
// }

// export function getDocumentDownloadToken(artifactId: string | Array<string>): Promise<IArtifactTokenResponse> {
//     return api.get<IArtifactTokenResponse>(
//         serviceUrl + '/artifacts/download-token?id=' + buildIdsQueryParamString(artifactId)
//     );
// }

// export function downloadMessagesArchive(artifactIds: Array<string>, token: string): Promise<void> {
//     return new Promise<void>((resolve, reject) => {
//         fetch(getDownloadDocumentsArchiveUrl(artifactIds, token), {
//             method: 'GET',
//             credentials: 'include',
//         })
//             .then((response) => response.blob())
//             .then((blob) => {
//                 const a = document.createElement('a');
//                 a.href = window.URL.createObjectURL(blob);
//                 a.download = 'Downloaded documents.zip';
//                 document.body.appendChild(a);
//                 a.click();
//                 URL.revokeObjectURL(a.href);
//                 a.remove();
//                 resolve();
//             })
//             .catch((err) => {
//                 reject(err);
//             });
//     });
// }
